/**
 * Approve and resume (R-HL-4): one click continues the same session (same conversation, same
 * folder) and lets the call it asked for run once. It confirms first, showing the whole call,
 * unless the person already sees all of it (resumeNeedsConfirm): high risk, anything that is not
 * a short one-line shell command (an Edit, a Write, an MCP call, a long or multi-line command).
 * A double click sends once: the button is busy until the hub answers. A redirect (a person's
 * denial with a note) offers it second, from a menu (ResumeMenu), and always confirms. What the
 * hub answered shows right under it (never a toast): continued, resumed, or approved only and why.
 */
import { useRef, useState } from 'react';
import { Loader, MoreHorizontal, RotateCcw, ShieldCheck } from '@orgus/ui/icons';
import type { ApprovalRedirect, ApprovalRequest, ApprovalResumeResponse } from '@orgus/protocol';
import { Button, ConfirmDialog, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, IconButton, Spinner, Tooltip, cn, toUiError } from '@orgus/ui';
import type { ButtonProps, UiError } from '@orgus/ui';
import { useMachine } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { usePersonName } from '../missions/people.ts';
import { commandOf, inputPreview, resumeApproval, resumeDisabledReason, resumeNeedsConfirm, useDecidedApproval, useMeId } from './blocked.ts';

/** The inline error under the button: what failed, why, and what to do next. */
export function ResumeError({ error, onRetry }: { error: UiError; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-start gap-x-3 gap-y-1 rounded-control bg-failed-soft px-3 py-2 text-sm">
      <p className="min-w-0 flex-1 text-fg">
        <span className="font-medium text-failed">{error.what}</span> {error.why} {error.next}
      </p>
      {onRetry && (
        <Button size="sm" variant="ghost" icon={<RotateCcw />} onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/**
 * The hub's answer, inline under the button: working (continued or resumed: it is on its way)
 * or waiting (approved only: the session has to be told, and the text says how).
 */
export function ResumeResult({ result }: { result: Pick<ApprovalResumeResponse, 'resumed' | 'message'> }) {
  const Icon = result.resumed ? Loader : ShieldCheck;
  return (
    <p role="status" className="flex min-w-0 items-start gap-2 text-sm text-fg">
      <span className="flex h-5 shrink-0 items-center">
        <Icon className={cn('size-4', result.resumed ? 'text-working motion-safe:animate-spin' : 'text-waiting')} aria-hidden />
      </span>
      <span className="min-w-0">{result.message}</span>
    </p>
  );
}

/** The confirm text: the exact command or path, everything else the call carries, and where it runs. */
export function ResumeConfirmBody({ a, machineName }: { a: Pick<ApprovalRequest, 'tool' | 'title' | 'subject'> & { input?: unknown }; machineName: string }) {
  const cmd = commandOf(a) ?? a.title;
  const extra = inputPreview(a);
  return (
    <div className="flex flex-col gap-2">
      <pre className="max-h-40 overflow-auto rounded-control bg-surface p-2 font-mono text-sm whitespace-pre-wrap break-all text-fg">{cmd}</pre>
      {extra && (
        <>
          <span className="text-sm font-medium text-fg-muted">What it sends</span>
          <pre aria-label="Full input" className="max-h-56 overflow-auto rounded-control bg-surface p-2 font-mono text-sm whitespace-pre-wrap break-all text-fg">{extra}</pre>
        </>
      )}
      <p className="text-sm text-fg-muted">
        {a.tool ?? 'The tool'} runs this once on {machineName}, in the same session and folder. A different command still asks you.
      </p>
    </div>
  );
}

/** Who redirected it and whether the session carried on: the confirm says so before it runs a denied call. */
export function resumeConfirmText(
  a: Pick<ApprovalRequest, 'risk'>,
  redirect?: Pick<ApprovalRedirect, 'by' | 'carriedOn'> | null,
  me?: string | null,
  display?: (id: string) => string,
): { title: string; description: string } {
  if (redirect) {
    const name = me && redirect.by === me ? 'You' : display ? display(redirect.by) : redirect.by;
    const who = `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
    return {
      title: 'Approve and resume the denied call?',
      description: `${who} denied this${redirect.carriedOn ? ' and the session carried on' : ''}; approving runs it once, where it stopped.`,
    };
  }
  return { title: a.risk === 'high' ? 'Approve and resume this high-risk call?' : 'Approve and resume this call?', description: 'The session continues where it stopped and runs exactly this once.' };
}

export interface ApproveAndResumeOptions {
  /** Always ask first (from a menu, or for a redirect), whatever resumeNeedsConfirm says. */
  alwaysConfirm?: boolean;
  /** A person's denial with a note: the confirm names it. */
  redirect?: ApprovalRedirect | null;
}

/**
 * Approve and resume as a hook, so a button or a menu item can start it: `start` sends at once
 * or opens the confirm, `dialog` is the confirm (render it outside any menu), `error` the
 * inline error. A null id is allowed (nothing to resume), so a page can call it unconditionally.
 */
export function useApproveAndResume(approvalId: string | null, opts: ApproveAndResumeOptions = {}) {
  const a = useDecidedApproval(approvalId);
  const machine = useMachine(a?.machineId);
  const canApprove = useCan('operator');
  const me = useMeId();
  const nameOf = usePersonName();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [result, setResult] = useState<Pick<ApprovalResumeResponse, 'resumed' | 'message'> | null>(null);
  const [confirming, setConfirming] = useState(false);
  const inflight = useRef(false);
  const machineName = machine?.name ?? 'Its machine';
  const reason = resumeDisabledReason({ canApprove, machineName, online: !!machine?.online, known: !!a });

  const send = async () => {
    if (!approvalId) return;
    setResult(null);
    setError(null);
    const r = await resumeApproval(approvalId);
    setResult({ resumed: r.resumed, message: r.message });
  };
  const run = async () => {
    if (inflight.current) return;
    inflight.current = true;
    setBusy(true);
    setError(null);
    try {
      await send();
    } catch (e) {
      setError(toUiError(e));
    } finally {
      inflight.current = false;
      setBusy(false);
    }
  };
  const confirmFirst = !a || opts.alwaysConfirm || !!opts.redirect || resumeNeedsConfirm(a);
  const start = () => (confirmFirst ? setConfirming(true) : void run());
  const text = a ? resumeConfirmText(a, opts.redirect, me, nameOf) : null;
  const dialog =
    a && text ? (
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        tone="primary"
        title={text.title}
        description={text.description}
        confirmLabel="Approve and resume"
        onConfirm={async () => {
          if (inflight.current) return;
          inflight.current = true;
          setBusy(true);
          try {
            await send();
          } finally {
            inflight.current = false;
            setBusy(false);
          }
        }}
      >
        <ResumeConfirmBody a={a} machineName={machineName} />
      </ConfirmDialog>
    ) : null;
  // The answer, inline wherever the control is (not with an error: the error says what failed).
  const answer = result && !error ? <ResumeResult result={result} /> : null;
  return { approval: a, reason, busy, error, result, answer, start, retry: () => void run(), dialog };
}

export interface ApproveAndResumeButtonProps {
  approvalId: string;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  className?: string;
}

export function ApproveAndResumeButton({ approvalId, size = 'sm', variant = 'primary', className }: ApproveAndResumeButtonProps) {
  const r = useApproveAndResume(approvalId);
  return (
    <span className={cn('inline-flex min-w-0 flex-col items-start gap-2', className)}>
      <Tooltip content={r.reason}>
        <span tabIndex={r.reason ? 0 : -1}>
          <Button size={size} variant={variant} icon={r.busy ? <Spinner /> : <ShieldCheck />} disabled={!!r.reason || r.busy} aria-busy={r.busy || undefined} onClick={r.start}>
            {r.busy ? 'Resuming' : 'Approve and resume'}
          </Button>
        </span>
      </Tooltip>
      {r.answer}
      {r.error && <ResumeError error={r.error} onRetry={r.retry} />}
      {r.dialog}
    </span>
  );
}

/**
 * Approve and resume, second to Mark as handled (a redirect): an icon button whose menu holds
 * it, always behind its confirm, so the call a person turned down never runs on one click.
 */
export function ResumeMenu({ approvalId, redirect, size = 'sm' }: { approvalId: string; redirect?: ApprovalRedirect | null; size?: 'sm' | 'md' }) {
  const r = useApproveAndResume(approvalId, { alwaysConfirm: true, redirect });
  return (
    <span className="inline-flex min-w-0 flex-col items-start gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton label="More actions" icon={<MoreHorizontal />} size={size} variant="secondary" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem icon={<ShieldCheck />} disabled={!!r.reason || r.busy} onSelect={r.start}>
            {r.busy ? 'Resuming' : 'Approve and resume…'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {r.answer}
      {r.error && <ResumeError error={r.error} onRetry={r.retry} />}
      {r.dialog}
    </span>
  );
}
